import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableInstance,
  MRT_TableOptions,
  useMaterialReactTable
} from "material-react-table";
import { Box, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import Axios from "axios";
import ExcelUploadModal from "../ExcelUpload/excel-upload.modal";
import { bulkFleetExcelUpload } from "../../utils/axios-requests";
import { exportExcelByDocument } from "../FacilityManager/Functions/exportExcelByDocument";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { FleetManagementRow, FleetManagementRowValues } from "./types/FleetManagement-row.types";
import { useRemoteFetchAdminLocations } from "../../hooks/Remote/Location/useRemoteFetchAdminLocations";
import { useRemoteFetchCompanies } from "../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { useRemoteFetchCompanyUnits } from "../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Stakeholder } from "../../features/FloorManager/typings/permission.entity";
import { useRemoteFetchUserInfoForAllUsers } from "../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { FleetSlidePanelCreateEditService } from "./FleetManagementTable.partial";
import { fleetRowValuesToFormData } from "./functions/FleetManagementTable.functions";
import TableDeleteConfirmDialog from "../Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { commonLocalization } from "../../functions/tableLocalization";
import { validateFleetTableRow } from "./functions/FleetManagement-validation.function";
import { mapNewRowToPartialFleetItem } from "./helper/mapNewRowToPartialFleetItem.function";
import { mapUpdatedRowToPartialFleetItem } from "./helper/mapUpdatedRowToPartialFleetItem.function";
import { permissionsEqual } from "./helpers/permissionsEqual.function";
import { ListTopToolbar } from "../Common/ListTablePartial/List-top-toolbar.partial";
import { ListRowActions } from "../Common/ListTablePartial/List-row-actions.partial";
import { LocationInventory } from "../../features/FloorManager/typings/location-inventory";
import { FleetAttachment, FleetStatus } from "./types/FleetManagement.types";
import { useRemoteFetchFleetData } from "../../hooks/Remote/Fleet/useRemoteFetchFleetData";
import { useRemoteCreateFleet } from "../../hooks/Remote/Fleet/useRemoteCreateFleet";
import { useRemoteUpdateFleet } from "../../hooks/Remote/Fleet/useRemoteUpdateFleet";
import { useRemoteDeleteFleet } from "../../hooks/Remote/Fleet/useRemoteDeleteFleet";
import { useRemoteUploadFleetAttachment } from "../../hooks/Remote/Fleet/useRemoteUploadFleetAttachment";
import { useRemoteDeleteFleetAttachment } from "../../hooks/Remote/Fleet/useRemoteDeleteFleetAttachment";
import { useRemoteCheckAffectedPermissions } from "../../hooks/Remote/Fleet/useRemoteCheckAffectedPermissions";
import { useRemoteCancelFleetBooking } from "../../hooks/Remote/Fleet/useRemoteCancelFleetBooking";
import { filterByBookable } from "./functions/filterByBookable.function";
import { PlaceBookingDetailDialog } from "../FacilityManager/Components/Views/SelectFloorPlanView/PlaceSelectionPrompt/PlaceBookingDetailDialog";
import {
  selectVehicleBooking,
  setInputs as setBookingInputs
} from "../../features/Booking-Form/slices/booking.slice";
import { getBookingModeFleetData } from "./functions/getBookingModeFleetData.function";
import { fleetBookingStartEnd } from "../BookingForm/BookingConfirmStrategy/vehicleBookingStrategy";
import { normalizePicturePaths } from "../ServiceManagement/Roomlayout/helpers/normalizePicturePaths.function";
import { EnlargedImageCarouselDialog } from "../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";
import { FleetManagementEventDetailDialog } from "./EventDetailDialog/FleetManagementEventDetailDialog.component";
import { FleetEventChildPartial } from "./EventDetailDialog/partial/FleetEventChild.partial";
import { useRemoteFetchBookingProperty } from "../../hooks/Remote/BookingProperty/useRemoteFetchBookingProperty";
import { BlockedUsersDialog } from "./BlockedUsersDialog/BlockedUsersDialog.component";
import { FleetManagementTableColumnToggles } from "./FleetManagementTable-ColumnToggles.partial";
import type { FleetColumnToggleId } from "./FleetManagementTable-ColumnToggles.partial";
import { getFleetTableColumns } from "./FleetManagementTable.columns";
import { useFleetEditDraft } from "./hooks/useFleetEditDraft.hook";
import { FleetToolbarTitleActions } from "./FleetToolbarTitleActions.partial";
import { FleetLocationFilterCards } from "./FleetLocationFilterCards.partial";
import { LogbookDialog } from "./LogbookDialog/LogbookDialog.component";
import { AffectedBookingsConfirmDialog } from "./AffectedBookingsDialog/AffectedBookingsConfirmDialog.component";
import { buildCheckAffectedBody } from "./helpers/buildCheckAffectedBody.function";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { useAffectedPermissionsConfirm } from "./hooks/useAffectedPermissionsConfirm.hook";
import { getFleetRowBackgroundColor } from "./functions/getFleetRowBackgroundColor";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { useRemoteFetchBookingFavoritesVehicles } from "../../hooks/Remote/BookingFavorites/useRemoteFetchBookingFavoritesVehicles";
import { useRemoteAddBookingFavorite } from "../../hooks/Remote/BookingFavorites/useRemoteAddBookingFavorite";
import { useRemoteRemoveBookingFavorite } from "../../hooks/Remote/BookingFavorites/useRemoteRemoveBookingFavorite";
import { getFleetAutoPreselection } from "./functions/getFleetAutoPreselection.function";
import { getSeeditMrtRowSx, withSeeditMrtInitialState } from "../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../themes/use-seedit-mrt-table-options";

export function getDefaultDateRange() {
  return {
    start: DateTime.now(),
    end: DateTime.now().plus({ days: 30 })
  };
}

export function getDateRangeFromBookingInputs(
  bookingInputs: RootState["booking"]["inputs"],
  fallback: { start: DateTime; end: DateTime },
  /** When set (e.g. login settings), used instead of `bookingInputs.timezone` for fleet date range. */
  zoneOverride?: string | null
): { start: DateTime; end: DateTime } {
  const { bookingFrom, bookingTo, bookingStart, bookingEnd, timezone } = bookingInputs;

  const startDateStr = bookingFrom ?? bookingTo;
  const endDateStr = bookingTo ?? bookingFrom;

  if (!startDateStr) {
    return fallback;
  }

  const zone = (zoneOverride ?? timezone) || undefined;

  const buildDateTime = (
    dateStr: string,
    timeStr: string | null | undefined,
    fallbackEndOfDay: boolean
  ): DateTime => {
    if (timeStr) {
      return DateTime.fromISO(`${dateStr}T${timeStr}`, { zone });
    }
    const base = DateTime.fromISO(dateStr, { zone });
    return fallbackEndOfDay ? base.endOf("day") : base.startOf("day");
  };

  const start = buildDateTime(startDateStr, bookingStart, false);
  const end = buildDateTime(endDateStr ?? startDateStr, bookingEnd ?? bookingStart, true);

  if (!start.isValid || !end.isValid) {
    return fallback;
  }

  return { start, end };
}

/**
 * @version 0.1.0
 * @TODO add teamBookingAllowed to login state
 * const isScheduleColleaguesCancelAllow = remoteColleagues?.find(col => col.userId === schedule.userBookedFor)?.teamBookingColleague ?? false;
 * const isColleagueScheduleEditable = isUpcomingSchedule &&schedule.userBookedFor !== userInformation.sub &&userInformation.company.meta.allowBookingCancellations && isScheduleColleaguesCancelAllow;
 */
export function FleetManagmentTable({
  isEditable,
  initialDateRange,
  nextStep
}: Readonly<{
  isEditable: boolean;
  /** Optional initial date range (e.g. for tests); defaults to getDefaultDateRange() */
  initialDateRange?: { start: DateTime; end: DateTime };
  nextStep?: () => void;
}>) {
  const {
    userInformation: { firstName, surname, email, sub, department, companyId },
    settings: { timezone: loginTimezone }
  } = useSelector((state: RootState) => state.login);
  const bookingInputs = useSelector((state: RootState) => state.booking.inputs);
  /** Fleet UI (columns, dialogs, booking date range) uses login timezone, not bookingInputs.timezone. */
  const fleetTimezone = loginTimezone ?? "UTC";
  const dispatch = useDispatch();

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const { dateFormat } = useTimeFormat();

  const [data, setData] = useState<FleetManagementRow[]>();

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectedRow, setSelectedRow] = useState<FleetManagementRow | null>(null);
  /** handle to select fleet item for booking */
  const [selectedFleetItem, setSelectedFleetItem] = useState<FleetManagementRow | null>(null);
  const autoPreselectedVehicleInventoryIdRef = useRef<number | null>(null);

  /** Mobile booking: track last tap for double-tap-to-confirm (rowId + timestamp). */
  const mobileConfirmTapRef = useRef<{ rowId: number; timestamp: number } | null>(null);
  const MOBILE_DOUBLE_TAP_MS = 400;

  /** Only show contact-admin snackbar once per conflict session. */
  const conflictSnackbarShownRef = useRef(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<number[] | null>(null);

  const {
    imageFiles,
    setImageFiles,
    showBiggerImages,
    setShowBiggerImages,
    picturePathsToDelete,
    setPicturePathsToDelete,
    attachmentDraft,
    setAttachmentDraft,
    propertiesDraft,
    setPropertiesDraft,
    validationErrors,
    setValidationErrors,
    resetDraft
  } = useFleetEditDraft();

  const [selectOccupiedBooking, setSelectOccupiedBooking] = useState<FleetManagementRow | null>(
    null
  );
  const [isAllEventDialogOpen, setIsAllEventDialogOpen] = useState(false);

  const [vehicleIdForBlockedUsersDialog, setVehicleIdForBlockedUsersDialog] = useState<
    number | null
  >(null);
  const [vehicleNameForBlockedUsersDialog, setVehicleNameForBlockedUsersDialog] = useState("");
  const [logbookDialog, setLogbookDialog] = useState<{
    vehicleId: number;
    vehicleName: string;
    licensePlate?: string;
  } | null>(null);

  // Date range state for filtering events (used by UI and calendar).
  // In booking mode (isEditable === false), derive from booking inputs (bookingFrom/bookingTo)
  // so availability in the list matches the selected booking dates.
  const [dateRange, setDateRange] = useState<{ start: DateTime; end: DateTime }>(() => {
    const base = initialDateRange ?? getDefaultDateRange();
    if (!isEditable) {
      return getDateRangeFromBookingInputs(bookingInputs, base, fleetTimezone);
    }
    return base;
  });

  // Column visibility: single source of truth so MRT's column visibility dropdown and our custom panel stay in sync
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    "mrt-row-expand": true,
    picturePaths: false,
    attachments: false,
    blockedUsers: false,
    logbook: false
  });

  const handleColumnToggle = (id: FleetColumnToggleId) => {
    setColumnVisibility(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Panel visibility: column toggles (shown/hidden via toolbar icon button)
  const [showColumnTogglesPanel, setShowColumnTogglesPanel] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  const filterBySelectedLocations = (items: FleetManagementRow[]): FleetManagementRow[] => {
    if (selectedLocationIds.length === 0) return items;
    return items.filter(item => {
      const locationId = item.location?.id;
      return typeof locationId === "number" && selectedLocationIds.includes(locationId);
    });
  };

  const toggleLocationFilter = (locationId: number) => {
    setSelectedLocationIds(prev =>
      prev.includes(locationId) ? prev.filter(id => id !== locationId) : [...prev, locationId]
    );
  };

  // Admin table: never use date range for fetch. Booking mode: use date range when active.
  const fleetFetchDateRange: { from: string; to: string } | null = isEditable
    ? null
    : (() => {
        const fromIso = dateRange.start.toISO();
        const toIso = dateRange.end.toISO();
        return fromIso && toIso ? { from: fromIso, to: toIso } : null;
      })();

  const {
    data: remoteFleetData,
    isLoading: isRemoteFleetDataLoading,
    refetch: refetchRemoteFleetData
  } = useRemoteFetchFleetData({
    dateRange: fleetFetchDateRange,
    excludeBlocked: !isEditable
  });

  const bookingRange = useMemo(
    () =>
      fleetBookingStartEnd(
        bookingInputs.bookingFrom ?? null,
        bookingInputs.bookingTo ?? null,
        bookingInputs.bookingStart ?? null,
        bookingInputs.bookingEnd ?? null,
        bookingInputs.allDay
      ),
    [
      bookingInputs.bookingFrom,
      bookingInputs.bookingTo,
      bookingInputs.bookingStart,
      bookingInputs.bookingEnd,
      bookingInputs.allDay
    ]
  );
  const userIdToCheck = bookingInputs.usersBookedFor?.[0]?.userId ?? sub;

  const {
    showAffectedDialog,
    affectedBookingsForDialog,
    pendingPermissionsSave,
    startConfirmFlow,
    applyPermissionsSave,
    clearConfirmFlow
  } = useAffectedPermissionsConfirm({
    updateFleet: async ({ id, formData }) => updateFleet({ id, formData }),
    refetchRemoteFleetData
  });

  const { mutateAsync: addFleet, status: addFleetStatus } = useRemoteCreateFleet();
  const { mutateAsync: updateFleet, status: updateFleetStatus } = useRemoteUpdateFleet();
  const { mutateAsync: removeFleet, status: removeFleetStatus } = useRemoteDeleteFleet();
  const { mutateAsync: checkAffectedPermissions } = useRemoteCheckAffectedPermissions();
  const { mutateAsync: cancelFleetBooking } = useRemoteCancelFleetBooking();
  const { mutateAsync: uploadAttachment } = useRemoteUploadFleetAttachment();
  const { mutateAsync: deleteAttachmentRemote } = useRemoteDeleteFleetAttachment();

  const { data: locations } = useRemoteFetchAdminLocations({ mode: "admin" });

  /** Full fleet list (before date-range filter) for the "All events" dialog so it shows all vehicles' upcoming events. */
  const fullFleetListForAllEvents = useMemo(() => {
    if (!remoteFleetData || !locations) return [];
    const filteredByRole = remoteFleetData.filter(item => {
      if (!isEditable) return filterByBookable({ item, sub, department, companyId });
      return true;
    });
    return filterBySelectedLocations(filteredByRole);
  }, [isEditable, remoteFleetData, locations, sub, department, companyId, selectedLocationIds]);

  const { data: remoteBookingProperty } = useRemoteFetchBookingProperty({
    propertyType: ["fleet"]
  });

  const { data: remoteCompanies } = useRemoteFetchCompanies();
  const { data: remoteDepartments } = useRemoteFetchCompanyUnits(Stakeholder.CostCenterUnit);
  const { data: remoteAllUsers } = useRemoteFetchUserInfoForAllUsers();

  const { data: vehicleFavoritesData } = useRemoteFetchBookingFavoritesVehicles(!isEditable);
  const vehicleFavoriteIds = useMemo(
    () => new Set(vehicleFavoritesData?.favorites?.map(f => f.bookingInventoryId) ?? []),
    [vehicleFavoritesData?.favorites]
  );
  const preferredVehicleInventoryId = useMemo(() => {
    const selectedVehicleId = bookingInputs.bookedVehicle?.id;
    if (typeof selectedVehicleId === "number") {
      return selectedVehicleId;
    }

    const assignedVehicleId = bookingInputs.usersBookedFor?.[0]?.bookingInventoryId;
    if (typeof assignedVehicleId === "number") {
      return assignedVehicleId;
    }

    const recommendedFavorite = vehicleFavoritesData?.recommendedFavorite;
    if (recommendedFavorite?.bookingType === BookingType.VEHICLE) {
      return recommendedFavorite.bookingInventoryId;
    }

    return null;
  }, [
    bookingInputs.bookedVehicle?.id,
    bookingInputs.usersBookedFor,
    vehicleFavoritesData?.recommendedFavorite
  ]);
  const addVehicleFavorite = useRemoteAddBookingFavorite();
  const removeVehicleFavorite = useRemoteRemoveBookingFavorite();
  const vehicleFavoriteActionPending =
    addVehicleFavorite.isLoading || removeVehicleFavorite.isLoading;

  const toggleVehicleFavorite = useCallback(
    async (vehicleInventoryId: number, currentlyFavorite: boolean) => {
      const payload = {
        bookingType: BookingType.VEHICLE,
        bookingInventoryId: vehicleInventoryId,
        floorInventoryId: null as number | null
      };
      if (currentlyFavorite) {
        await removeVehicleFavorite.mutateAsyncSafe(payload);
      } else {
        await addVehicleFavorite.mutateAsyncSafe(payload);
      }
    },
    [addVehicleFavorite, removeVehicleFavorite]
  );

  const deleteSelectedImage = (picturePath: string, rowOriginal: FleetManagementRow) => {
    setPicturePathsToDelete(prev => [...prev, picturePath]);
    if (Array.isArray(rowOriginal.picturePaths)) {
      rowOriginal.picturePaths = rowOriginal.picturePaths.filter(path => path !== picturePath);
    }
  };

  const uploadAttachmentsToFleet = async (
    vehicleId: number,
    attachments: FleetAttachment[]
  ): Promise<void> => {
    if (!attachments.length) return;

    for (const attachment of attachments) {
      try {
        await uploadAttachment({ vehicleId, attachment });
      } catch {
        // Error snackbar is handled inside the hook; continue with next attachment.
      }
    }
  };

  const deleteFleetAttachment = async (vehicleId: number, attachmentId: number) => {
    try {
      await deleteAttachmentRemote({ vehicleId, attachmentId });
    } catch {
      // Snackbar handled in hook
    } finally {
      setAttachmentDraft(prev => prev.filter(att => att.id !== attachmentId));
      refetchRemoteFleetData();
    }
  };

  const column = useMemo<MRT_ColumnDef<FleetManagementRow>[]>(() => {
    return getFleetTableColumns({
      isEditable,
      t,
      language: i18n.language,
      dateFormat,
      timezone: fleetTimezone,
      validationErrors,
      setValidationErrors,
      locations,
      remoteBookingProperty,
      remoteCompanies,
      remoteDepartments,
      remoteAllUsers,
      attachmentDraft,
      setAttachmentDraft,
      propertiesDraft,
      setPropertiesDraft,
      imageFiles,
      setImageFiles,
      deleteSelectedImage,
      setShowBiggerImages,
      setVehicleIdForBlockedUsersDialog,
      setVehicleNameForBlockedUsersDialog,
      setLogbookDialog,
      setSelectedRow,
      deleteFleetAttachment,
      vehicleFavoriteIds,
      onToggleVehicleFavorite: toggleVehicleFavorite,
      vehicleFavoriteActionPending
    });
  }, [
    isEditable,
    t,
    i18n.language,
    dateFormat,
    fleetTimezone,
    validationErrors,
    locations,
    remoteBookingProperty,
    remoteCompanies,
    remoteDepartments,
    remoteAllUsers,
    attachmentDraft,
    propertiesDraft,
    imageFiles,
    vehicleFavoriteIds,
    toggleVehicleFavorite,
    vehicleFavoriteActionPending
  ]);

  // Keep date range in sync with booking form dates in booking mode so that when the
  // user changes bookingFrom/bookingTo, the fetched fleet data and computed status follow.
  useEffect(() => {
    if (isEditable) return;
    const fallback = initialDateRange ?? getDefaultDateRange();
    const nextRange = getDateRangeFromBookingInputs(bookingInputs, fallback, fleetTimezone);

    setDateRange(prev =>
      prev.start.equals(nextRange.start) && prev.end.equals(nextRange.end) ? prev : nextRange
    );
  }, [
    isEditable,
    bookingInputs.bookingFrom,
    bookingInputs.bookingTo,
    bookingInputs.bookingStart,
    bookingInputs.bookingEnd,
    fleetTimezone,
    initialDateRange
  ]);

  // In booking mode, refetch fleet data when the table is shown so booked vehicles and status are up to date.
  useEffect(() => {
    if (!isEditable) refetchRemoteFleetData();
  }, [isEditable, refetchRemoteFleetData]);

  useEffect(() => {
    if (isEditable) {
      // Admin table: no date range filtering; show all vehicles from fetch.
      if (!remoteFleetData || !locations) return;
      setData(filterBySelectedLocations(remoteFleetData));
      return;
    }
    if (!remoteFleetData) return;
    const filteredByRole = remoteFleetData.filter(item =>
      filterByBookable({ item, sub, department, companyId })
    );
    const { data: nextData, hasConflict } = getBookingModeFleetData({
      remoteFleetData,
      filteredByRole,
      bookingRangeStartIso: bookingRange.start,
      bookingRangeEndIso: bookingRange.end,
      userIdToCheck,
      filterBySelectedLocations
    });

    if (hasConflict) {
      if (bookingInputs.hasCheckedInFleetConflict !== true) {
        dispatch(setBookingInputs({ hasCheckedInFleetConflict: true }));
      }
      if (!conflictSnackbarShownRef.current) {
        enqueueSnackbar(t("_fleetBookingEventContactAdminExistingBookingCheckedIn"), {
          variant: "warning"
        });
        conflictSnackbarShownRef.current = true;
      }
      setData(prev => (prev?.length === 0 ? prev : []));
      return;
    }

    conflictSnackbarShownRef.current = false;
    if (bookingInputs.hasCheckedInFleetConflict === true) {
      dispatch(setBookingInputs({ hasCheckedInFleetConflict: false }));
    }
    setData(nextData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch/enqueueSnackbar/t are stable; omitting to avoid effect loop when conflict state is dispatched
  }, [
    isEditable,
    sub,
    department,
    companyId,
    remoteFleetData,
    locations,
    selectedLocationIds,
    bookingRange.start,
    bookingRange.end,
    userIdToCheck
  ]);

  // after deleting a row, refresh the list
  useEffect(() => {
    if (removeFleetStatus === "success") {
      const multiDeleted =
        deletionSelected && deletionSelected.length > 1
          ? t("_deletedMultiFleet")
          : t("_deletedSingleFleet");

      enqueueSnackbar(t("Successfully deleted fleet", { unit: multiDeleted }), {
        variant: "success"
      });
      setIsDeleteModalOpen(false);
      refetchRemoteFleetData();
      setDeletionSelected(null);
    }
  }, [removeFleetStatus]);

  // after adding a row, refresh the list
  useEffect(() => {
    if (addFleetStatus === "success") refetchRemoteFleetData();
  }, [addFleetStatus]);

  // after updating a row, refresh the list
  useEffect(() => {
    if (updateFleetStatus === "success") refetchRemoteFleetData();
  }, [updateFleetStatus]);

  // When fleet data updates (e.g. after creating a booking), keep selectedRow in sync so the event detail dialog shows the latest schedule
  useEffect(() => {
    if (!selectedRow || !data?.length) return;
    const fresh = data.find(r => r.id === selectedRow.id);
    if (fresh && fresh !== selectedRow) setSelectedRow(fresh);
  }, [data, selectedRow?.id]);

  const resetTableCreateEdit = () => {
    resetDraft();
  };

  const updateFleetLocation = (
    values: FleetManagementRowValues,
    locations: LocationInventory[] | undefined
  ) => {
    const upd = values;
    const selectedLoc = locations?.find(loc => values["location.name"] === loc.name);

    upd["location"] = selectedLoc;
    upd["location.id"] = selectedLoc?.id;
    upd["location.name"] = values["location.name"];

    return upd;
  };

  const updateFleetPermissions = (
    values: FleetManagementRowValues,
    rowOriginal: FleetManagementRow
  ) => {
    const upd = values;

    const affectedCompanies = values.affectedCompanies ?? rowOriginal.affectedCompanies ?? [];
    const affectedDepartments = values.affectedDepartments ?? rowOriginal.affectedDepartments ?? [];
    const affectedUsers = values.affectedUsers ?? rowOriginal.affectedUsers ?? [];

    upd["affectedCompanies"] = affectedCompanies;
    upd["affectedDepartments"] = affectedDepartments;
    upd["affectedUsers"] = affectedUsers;
    upd["fleetPermissions"] = {
      id: values["id"],
      companyIds: affectedCompanies,
      costCenterUnitIds: affectedDepartments,
      userIds: affectedUsers
    };

    return upd;
  };

  const updateFleetPicturepath = (values: FleetManagementRowValues) => {
    const upd = { ...values };

    upd.picturePaths = normalizePicturePaths(upd.picturePaths, picturePathsToDelete);
    return upd;
  };

  /** deletes fleet vehicles (one DELETE per id; backend has no bulk delete) */
  const handleRowDelete = async (rowUids: number[]) => {
    for (const id of rowUids) {
      await removeFleet(id);
    }
    table.resetRowSelection();
    setIsDeleteModalOpen(false);
    setDeletionSelected(null);
    refetchRemoteFleetData();
  };

  /** adds a vehicle */
  const handleRowAdd: MRT_TableOptions<FleetManagementRow>["onCreatingRowSave"] = async ({
    row,
    values,
    exitCreatingMode
  }) => {
    const newValidationErrors = validateFleetTableRow(values);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    const enrichedValues = {
      ...values,
      attachments: attachmentDraft,
      properties: propertiesDraft
    };

    const locUpdated = updateFleetLocation(enrichedValues, locations);
    const permUpdated = updateFleetPermissions(locUpdated, row.original);
    const pictureUpdated = updateFleetPicturepath(permUpdated);
    const newVal = mapNewRowToPartialFleetItem(pictureUpdated);

    const newValueFormData = fleetRowValuesToFormData(newVal, row.original, sub, imageFiles);

    const created = await addFleet(newValueFormData);
    if (created?.id && attachmentDraft.length > 0) {
      await uploadAttachmentsToFleet(created.id, attachmentDraft);
    }
    resetTableCreateEdit();
    exitCreatingMode();
    table.setCreatingRow(null);
    refetchRemoteFleetData();
  };

  const exitEditingModeRef = useRef<(() => void) | null>(null);

  /** updates a vehicle; before saving permissions calls check-affected and requires confirmation, then PATCH (permissions in vehicle inventory) */
  const handleRowUpdate: MRT_TableOptions<FleetManagementRow>["onEditingRowSave"] = async ({
    row,
    values,
    exitEditingMode
  }) => {
    exitEditingModeRef.current = exitEditingMode;

    const newValidationErrors = validateFleetTableRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    const enrichedValues = {
      ...values,
      attachments: attachmentDraft,
      properties: propertiesDraft
    };

    const locUpdated = updateFleetLocation(enrichedValues, locations);
    const permUpdated = updateFleetPermissions(locUpdated, row.original);
    const pictureUpdated = updateFleetPicturepath(permUpdated);
    const newVal = mapUpdatedRowToPartialFleetItem(pictureUpdated, row.original);

    const newValueFormData = fleetRowValuesToFormData(newVal, row.original, sub, imageFiles);

    const permissionsChanged = !permissionsEqual(
      permUpdated.fleetPermissions ?? {},
      row.original.fleetPermissions ?? {}
    );

    /**
     * if permissions changed, check if there are affected bookings and show the affected bookings dialog with the affected bookings
     * because we need to check if the permissions are changed and if so, we need to check if there are affected bookings and show the affected bookings dialog with the affected bookings
     */
    if (permissionsChanged) {
      const nextPerms = permUpdated.fleetPermissions;
      if (!nextPerms) return;

      const checkAffectedBody = buildCheckAffectedBody(nextPerms);

      try {
        const affected = await checkAffectedPermissions({
          vehicleId: row.original.id,
          body: checkAffectedBody
        });

        if (affected.length > 0) {
          startConfirmFlow({
            vehicleId: row.original.id,
            vehicleName: row.original.name,
            formData: newValueFormData,
            affectedBookings: affected
          });
          return;
        }
      } catch {
        return;
      }
    }

    resetTableCreateEdit();
    exitEditingMode();

    await updateFleet({ id: row.original.id, formData: newValueFormData });
    if (attachmentDraft.length > 0) {
      await uploadAttachmentsToFleet(row.original.id, attachmentDraft);
    }

    table.setEditingRow(null);
    refetchRemoteFleetData();
  };

  /** Dispatches vehicle selection to booking slice (next step). Used by desktop single-click and mobile second-tap. */
  const confirmVehicleSelection = useCallback(
    (row: FleetManagementRow) => {
      dispatch(
        selectVehicleBooking({
          usersBookedFor: {
            firstName,
            surname,
            email,
            userId: sub,
            isExternal: false,
            bookingInventoryId: row.id
          },
          vehicleInfo: row
        })
      );
    },
    [dispatch, firstName, surname, email, sub]
  );

  useEffect(() => {
    const autoPreselection = getFleetAutoPreselection({
      isEditable,
      data,
      preferredVehicleInventoryId,
      autoPreselectedVehicleInventoryId: autoPreselectedVehicleInventoryIdRef.current
    });
    if (autoPreselection.kind === "skip") {
      return;
    }
    const { preferredVehicle } = autoPreselection;

    setSelectedFleetItem(preferredVehicle);

    if (bookingInputs.bookedVehicle?.id !== preferredVehicle.id) {
      confirmVehicleSelection(preferredVehicle);
    }

    autoPreselectedVehicleInventoryIdRef.current = preferredVehicleInventoryId;
  }, [
    isEditable,
    data,
    preferredVehicleInventoryId,
    bookingInputs.bookedVehicle?.id,
    confirmVehicleSelection
  ]);

  /** selects a fleet item for booking (desktop: single click = select+confirm or unselect) */
  const handleRowSelect = (row: FleetManagementRow) => {
    if (row.status !== FleetStatus.Available) return;

    setSelectedFleetItem(row);
    confirmVehicleSelection(row);

    // select again => unselect
    if (row.id === selectedFleetItem?.id) {
      setSelectedFleetItem(null);
      dispatch(
        selectVehicleBooking({
          usersBookedFor: {
            firstName,
            surname,
            email,
            userId: sub,
            isExternal: false,
            bookingInventoryId: undefined
          },
          vehicleInfo: undefined
        })
      );
    }
  };

  /** Booking mode row interaction: desktop = single-click confirm; mobile = double-tap to confirm. */
  const handleBookingModeRowInteraction = (row: FleetManagementRow) => {
    if (row.status !== FleetStatus.Available) return;

    if (isMobile) {
      const now = Date.now();
      const isSameRowAsSelected = selectedFleetItem?.id === row.id;
      const isSecondTapWithinWindow =
        mobileConfirmTapRef.current?.rowId === row.id &&
        now - mobileConfirmTapRef.current.timestamp <= MOBILE_DOUBLE_TAP_MS;

      if (isSameRowAsSelected && isSecondTapWithinWindow) {
        mobileConfirmTapRef.current = null;
        confirmVehicleSelection(row);
        nextStep?.();
        return;
      }

      if (isSameRowAsSelected) {
        setSelectedFleetItem(null);
        mobileConfirmTapRef.current = null;

        dispatch(
          selectVehicleBooking({
            usersBookedFor: {
              firstName,
              surname,
              email,
              userId: sub,
              isExternal: false,
              bookingInventoryId: undefined
            },
            vehicleInfo: undefined
          })
        );
        return;
      }

      setSelectedFleetItem(row);
      mobileConfirmTapRef.current = { rowId: row.id, timestamp: now };
      return;
    }

    handleRowSelect(row);
  };

  /** opens the all fleet events dialog */
  const openAllFleetEvents = () => setIsAllEventDialogOpen(true);

  const fleetExcelLang = i18n.language?.slice(0, 2) ?? "en";
  const handleExportFleet = async () => {
    try {
      const response = await Axios.get("/fleet/vehicles/export", {
        params: { format: "xlsx", lang: fleetExcelLang },
        responseType: "blob"
      });
      const blobUrl = URL.createObjectURL(response.data);
      /** later, use exportToExcel from user management table for columns and translations */
      exportExcelByDocument(blobUrl, t("Drawer_Fleet"));
      URL.revokeObjectURL(blobUrl);
    } catch {
      enqueueSnackbar(t("Failed to export fleet vehicles"), { variant: "error" });
    }
  };

  const tableState = {
    columnVisibility: {
      ...columnVisibility,
      "mrt-row-expand": isEditable
    },
    rowSelection:
      !isEditable && selectedFleetItem ? { [selectedFleetItem.id]: true } : rowSelection,
    isLoading: isRemoteFleetDataLoading,
    isSaving:
      addFleetStatus === "loading" ||
      updateFleetStatus === "loading" ||
      removeFleetStatus === "loading"
  };

  const tableCallbacks: Pick<
    MRT_TableOptions<FleetManagementRow>,
    | "onCreatingRowCancel"
    | "onCreatingRowSave"
    | "onEditingRowCancel"
    | "onEditingRowSave"
    | "onRowSelectionChange"
    | "onColumnVisibilityChange"
  > = {
    onCreatingRowCancel: resetTableCreateEdit,
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: resetTableCreateEdit,
    onEditingRowSave: handleRowUpdate,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: updater => {
      setColumnVisibility(prev => (typeof updater === "function" ? updater(prev) : updater));
    }
  };

  const tableRenderers: Pick<
    MRT_TableOptions<FleetManagementRow>,
    | "renderRowActions"
    | "renderTopToolbar"
    | "renderCreateRowDialogContent"
    | "renderEditRowDialogContent"
  > = {
    renderRowActions: ({ row, table }) => (
      <ListRowActions
        title="fleet-mgmt"
        row={row as unknown as MRT_Row<any>}
        table={table as unknown as MRT_TableInstance<any>}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        setDeletionSelected={setDeletionSelected}
      />
    ),
    renderTopToolbar: ({ table }) => (
      <ListTopToolbar
        title={t("Drawer_Fleet")}
        createBtnLabel={t("_addFleet")}
        table={table as unknown as MRT_TableInstance<any>}
        isEditable={isEditable}
        disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
        onDelete={() => {
          setIsDeleteModalOpen(true);
          const selected = table.getSelectedRowModel().rows.map(row => row.original.id);
          setDeletionSelected(selected);
        }}
        titleActions={
          <FleetToolbarTitleActions
            calendarNode={
              <FleetEventChildPartial
                part="calendar"
                isEditable={isEditable}
                openAllFleetEvents={openAllFleetEvents}
              />
            }
            showColumnTogglesPanel={showColumnTogglesPanel}
            onToggleColumnTogglesPanel={() => setShowColumnTogglesPanel(prev => !prev)}
            isEditable={isEditable}
          />
        }
        importExportNode={
          isEditable ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ExcelUploadModal
                uploadCall={bulkFleetExcelUpload}
                triggerCall={refetchRemoteFleetData}
                onFinishUpload={() => {
                  enqueueSnackbar(t("Successfully imported fleet vehicles"), {
                    variant: "success"
                  });
                }}
              />
              <Tooltip title={<Typography>{t("Export")}</Typography>}>
                <IconButton data-testid="fleet-export-btn" onClick={handleExportFleet} size="large">
                  <FileDownload />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null
        }
        childComponent={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              width: "100%"
            }}
          >
            {!isEditable && (
              <FleetLocationFilterCards
                locations={(locations ?? []).map(location => ({
                  id: location.id,
                  name: location.name,
                  picturePath: location.picturePath ?? null
                }))}
                selectedLocationIds={selectedLocationIds}
                onToggleLocation={toggleLocationFilter}
                onClear={() => setSelectedLocationIds([])}
              />
            )}
            {showColumnTogglesPanel && (
              <FleetManagementTableColumnToggles
                visible={{
                  picturePaths: columnVisibility.picturePaths,
                  attachments: columnVisibility.attachments,
                  blockedUsers: columnVisibility.blockedUsers,
                  logbook: columnVisibility.logbook
                }}
                onToggle={handleColumnToggle}
                isEditable={isEditable}
              />
            )}
          </Box>
        }
      />
    ),
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <FleetSlidePanelCreateEditService
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("_addFleetSlidePanel")}
      />
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <FleetSlidePanelCreateEditService
        internalEditComponents={internalEditComponents}
        headline={t("_editFleetSlidePanel")}
        table={table}
        row={row}
      />
    )
  };

  const editableColumnPinningOpt = isEditable
    ? ["mrt-row-actions", "name"]
    : ["vehicleFavorite", "mrt-row-select", "name"];
  const responsiveColumnPinningOpt = isMobile ? [] : editableColumnPinningOpt;
  const seeditMrtOptions = useSeeditMrtTableOptions<FleetManagementRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableEditing: isEditable,
    enableColumnPinning: true,
    enableRowSelection: isEditable,
    muiTableBodyRowProps: ({ row }) => {
      const seeditRowSx = getSeeditMrtRowSx(theme, { row });
      if (isEditable) {
        return { sx: seeditRowSx };
      }
      return {
        onClick: e => {
          if ((e.target as HTMLElement).closest("button, input, textarea, select")) {
            return;
          }
          handleBookingModeRowInteraction(row.original);
        },
        onKeyDown: e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBookingModeRowInteraction(row.original);
          }
        },
        role: "button",
        tabIndex: 0,
        sx: {
          ...seeditRowSx,
          backgroundColor: getFleetRowBackgroundColor(
            isEditable,
            row.getIsSelected(),
            row.original
          ),
          cursor: row.original.status === FleetStatus.Available ? "pointer" : "default",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
        }
      };
    },
    autoResetPageIndex: false,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    positionToolbarAlertBanner: "none",
    getRowId: row => row.id?.toString(),
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
      sorting: isEditable ? [] : [{ id: "status", desc: false }],
      columnPinning: {
        left: responsiveColumnPinningOpt
      }
    }),
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 30],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: isEditable
        ? t("_fleetManagement_noVehicles")
        : t("_fleetManagement_noAvailableVehiclesForBooking")
    },
    ...tableCallbacks,
    state: tableState,
    ...tableRenderers
  });

  const editingRowId = table.getState().editingRow?.id;

  useEffect(() => {
    const editingRow = table.getState().editingRow;
    if (!editingRow) {
      setAttachmentDraft([]);
      setPropertiesDraft([]);
      return;
    }

    if (editingRow.id === "mrt-row-create") {
      setAttachmentDraft([]);
      setPropertiesDraft([]);
      return;
    }

    setAttachmentDraft(editingRow.original.attachments ?? []);
    setPropertiesDraft(editingRow.original.properties ?? []);
  }, [editingRowId, table]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactTable table={table} />
      </LocalizationProvider>

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDescNeutral", { title: t("_deleteConfirmFleet") })
            : t("_tableDeletionConfirmationDesc")
        }
        deleteStatus={removeFleetStatus}
      />

      {/* show enlarged image carousel dialog */}
      <EnlargedImageCarouselDialog
        handleClose={() => setShowBiggerImages(null)}
        isOpen={!!showBiggerImages}
        picturePaths={showBiggerImages?.reverse() ?? null}
        isLoading={!showBiggerImages}
      />

      {/* show occupied booking */}
      <PlaceBookingDetailDialog
        isDialogOpen={selectOccupiedBooking !== null}
        setIsDialogOpen={() => setSelectOccupiedBooking(null)}
        timezone={fleetTimezone}
        occupiedStatus={selectOccupiedBooking?.occupiedBooking}
      />

      {/* show blocked users dialog */}
      <BlockedUsersDialog
        vehicleId={vehicleIdForBlockedUsersDialog}
        vehicleName={vehicleNameForBlockedUsersDialog}
        allUsers={remoteAllUsers}
        onClose={() => setVehicleIdForBlockedUsersDialog(null)}
      />

      {/* show logbook dialog */}
      <LogbookDialog
        vehicleId={logbookDialog?.vehicleId ?? null}
        vehicleName={logbookDialog?.vehicleName ?? ""}
        licensePlate={logbookDialog?.licensePlate}
        onClose={() => setLogbookDialog(null)}
      />

      {/* show affected bookings dialog */}
      <AffectedBookingsConfirmDialog
        open={showAffectedDialog}
        affectedBookings={affectedBookingsForDialog}
        vehicleName={pendingPermissionsSave?.vehicleName}
        allUsers={remoteAllUsers ?? undefined}
        onConfirm={async () => {
          const bookingIdsToCancel = affectedBookingsForDialog
            .map(b => b.id)
            .filter((id): id is number => typeof id === "number");
          await applyPermissionsSave();
          for (const bookingId of bookingIdsToCancel) {
            await cancelFleetBooking(bookingId);
          }
          clearConfirmFlow();
          resetTableCreateEdit();
          exitEditingModeRef.current?.();
          table.setEditingRow(null);
        }}
        onCancel={() => {
          clearConfirmFlow();
          resetTableCreateEdit();
          exitEditingModeRef.current?.();
          table.setEditingRow(null);
        }}
      />

      {/* show Fleet event detail dialog - Booking, Blocked, or etc */}
      <FleetManagementEventDetailDialog
        open={!!selectedRow || isAllEventDialogOpen}
        isEditable={isEditable}
        onClose={() => {
          setSelectedRow(null);
          setIsAllEventDialogOpen(false);
        }}
        table={table}
        selectedRow={selectedRow}
        showAllEvents={isAllEventDialogOpen}
        allFleetData={isAllEventDialogOpen ? fullFleetListForAllEvents : undefined}
        locationOptions={locations}
        onFleetDataRefetch={refetchRemoteFleetData}
        bookingDateRange={isEditable ? undefined : dateRange}
      />
    </>
  );
}
